/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import useEnterKey from '@/hooks/useEnterKey';
import useOpen from '@/hooks/useOpen';
import { useTranslations } from '@/hooks/useTranslations';
import {
    Button,
    Dialog,
    FlexBox,
    FlexBoxAlignItems,
    FlexBoxJustifyContent,
    Icon,
    ListItemStandard,
    ListItemStandardDomRef,
    Text,
    Title,
} from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { useRef } from 'react';

type Props = {
    onOk: VoidFunction;
};

export function SignOut({ onOk }: Props) {
    const { t } = useTranslations();
    const signOutButtonLabel = 'SIGNOUT_BUTTON';
    const [open, toggle] = useOpen();
    const signOutItemRef = useRef<ListItemStandardDomRef>(null);
    useEnterKey(toggle, signOutItemRef);

    return (
        <>
            <ListItemStandard
                ref={signOutItemRef}
                data-testid="sign-out-wrapper"
                icon="log"
                onClick={toggle}
                style={{ height: ThemingParameters.sapContent_Space_M, cursor: 'pointer' }}
            >
                <Text style={{ cursor: 'pointer' }}>{t(signOutButtonLabel)}</Text>
            </ListItemStandard>
            <Dialog
                open={open}
                accessibleName={t(signOutButtonLabel)}
                header={
                    <FlexBox
                        style={{ padding: '0.5rem 0' }}
                        fitContainer
                        alignItems={FlexBoxAlignItems.Start}
                        justifyContent={FlexBoxJustifyContent.Start}
                        gap={8}
                    >
                        <Icon name="sys-help-2" />
                        <Title>{t(signOutButtonLabel)}</Title>
                    </FlexBox>
                }
                footer={
                    <FlexBox gap={5} justifyContent="End" alignItems="Center" style={{ width: '100%' }}>
                        <Button
                            design="Emphasized"
                            onClick={onOk}
                            data-testid="ok-button-wrapper"
                            style={{ marginRight: ThemingParameters.sapContent_Margin_Tiny }}
                        >
                            {t('OK_BUTTON')}
                        </Button>
                        <Button design="Transparent" data-testid="cancel-button-wrapper" onClick={toggle}>
                            {t('CANCEL_BUTTON')}
                        </Button>
                    </FlexBox>
                }
            >
                <Text style={{ margin: ThemingParameters.sapContent_Margin_Tiny }}>{t('SIGNOUT.CONFIRMATION.DIALOG.CONTENT')}</Text>
            </Dialog>
        </>
    );
}
